import { useEffect, useState } from "react";

/* ─────────────────────────────────────────────
   IMAGE IMPORTS — src/assets/images/blogs/
───────────────────────────────────────────── */
import HeroImg             from "../../assets/images/blogs/energy-dna-green-transition-detail.webp";
import HeroImgMobile       from "../../assets/images/blogs/energy-dna-green-transition-mobile.webp";
import NewsletterBgDesktop from "../../assets/images/blogs/newsletter-bg.webp";
import NewsletterMicMobile from "../../assets/images/blogs/newsletter-bg-mobile.webp";

/* ─────────────────────────────────────────────
   WhyWeMustUnlockEnergysDNAToEnableAGreenTransition.jsx
   Prefix      : gt-
   Fonts       : Montserrat (headings) | Roboto (body)
   Purple      : #743FAC
   BG main     : #1A1A1A
   Inner pad   : 102px desktop | 32px tab | 16px mobile
   Breaks      : 768px | 480px
   Anim        : IntersectionObserver fade-up
                 delays 0.08s / 0.18s / 0.28s / 0.38s

   DIFF vs other pages:
   - Hero title     : CENTERED (2 lines)
   - gt-subhead--bold : bold opening sentence (16px 700, no margin-top)
   - No subheads, no images in body, no bullets
   - Newsletter     : prop-based isMobile, Subscribe button
─────────────────────────────────────────────── */

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Roboto:wght@300;400;500;700&display=swap');

  .gt-wrap *, .gt-wrap *::before, .gt-wrap *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  .gt-wrap {
    font-family: 'Roboto', sans-serif;
    background: #1A1A1A;
    color: #ffffff;
    width: 100%;
    overflow-x: hidden;
  }
  .gt-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
    width: 100%;
  }
  .gt-content-col { width: 100%; }

  /* ── SCROLL FADE-UP ── */
  .gt-fade-up {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .gt-fade-up.gt-in { opacity: 1; transform: translateY(0); }
  .gt-delay-1 { transition-delay: 0.08s; }
  .gt-delay-2 { transition-delay: 0.18s; }
  .gt-delay-3 { transition-delay: 0.28s; }
  .gt-delay-4 { transition-delay: 0.38s; }

  /* ══════════════════════════════════════
     HERO — centered title
  ══════════════════════════════════════ */
  .gt-hero { width: 100%; background: #1A1A1A; }
  .gt-hero__title-bar {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0px;
  }
  .gt-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    text-align: center;
  }
  .gt-hero__img-wrap {
    width: 100%;
    max-height: 600px;
    overflow: hidden;
  }
  .gt-hero__img-wrap img {
    width: 100%;
    height: 100%;
    max-height: 600px;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  /* ══════════════════════════════════════
     BODY CONTENT
  ══════════════════════════════════════ */
  .gt-body-section {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0 100px;
  }

  /* bold opening sentence — image 2, no margin-top */
  .gt-subhead--bold {
    font-family: 'Roboto', sans-serif;
    font-size: 20px;
    font-weight: 700;
    line-height: 160%;
    color: #ffffff;
    margin-bottom: 24px;
  }

  /* regular paragraph */
  .gt-para {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }
    .gt-para:last-child { margin-bottom: 0px;}

  /* ══════════════════════════════════════
     NEWSLETTER
  ══════════════════════════════════════ */
  .gt-newsletter {
    width: 100%;
    position: relative;
    overflow: hidden;
    min-height: 500px;
    display: flex;
    align-items: center;
  }
  .gt-newsletter__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
   
    object-position: center;
    z-index: 0;
  }
  .gt-newsletter__overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
  }
  .gt-newsletter__inner {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 0px 102px;
  }
  .gt-newsletter__heading {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 120%;
    margin-bottom: 14px;
  }
  .gt-newsletter__sub {
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    color: rgba(255,255,255,0.72);
    line-height: 160%;
    margin-bottom: 24px;
  }
  .gt-newsletter__form {
    display: flex;
    align-items: stretch;
    max-width: 552px;
    gap: 14px;
  }
  .gt-newsletter__input {
    flex: 1;
    height: 48px;
    background: #1A1A1A;
    border: 1px solid #ffffff;
    border-radius: 5px;
    padding: 15px 14px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    outline: none;
  }
  .gt-newsletter__input::placeholder { color: #FFFFFF; }
  .gt-newsletter__btn {
    height: 50px;
    padding: 15px 30px;
    background: #743FAC;
    border: none;
    border-radius: 5px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: background 0.25s ease;
  }
  .gt-newsletter__btn:hover { background: #5e2e90; }
  .gt-newsletter__btn-arrow {
    display: inline-flex;
    align-items: center;
    transition: transform 0.2s ease;
  }
  .gt-newsletter__btn:hover .gt-newsletter__btn-arrow { transform: translateX(4px); }
  .gt-newsletter__mic-img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }
  .gt-newsletter__content { padding: 0; }

  /* ══════════════════════════
     RESPONSIVE 768px
  ══════════════════════════ */
  @media (max-width: 768px) {
    .gt-inner { padding-left: 32px; padding-right: 32px; }
    .gt-hero__img-wrap img { max-height: 350px; }
    .gt-hero__title-bar { padding: 24px 0px 20px; }
    .gt-hero__title { font-size: 24px; }
    .gt-body-section { padding: 40px 0 48px; }
    .gt-para { margin-bottom: 18px; font-size: 15px; }
    .gt-subhead--bold { font-size: 15px; margin-bottom: 18px; }

    .gt-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .gt-newsletter__mic-img { width: 100%; height: 350px; object-fit: cover; }
    .gt-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .gt-newsletter__content {
      padding: 40px 24px 48px;
      text-align: center;
    }
    .gt-newsletter__heading { font-size: 24px; }
    .gt-newsletter__sub { font-size: 14px; margin: 0 auto 24px; }
    .gt-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 12px;
    }
    .gt-newsletter__input { border-radius: 5px; height: 44px; }
    .gt-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 44px;
    }
  }

  /* ══════════════════════════
     RESPONSIVE 480px
  ══════════════════════════ */
  @media (max-width: 480px) {
    .gt-inner { padding-left: 16px; padding-right: 16px; }
    .gt-hero__title-bar { padding: 30px 0px; }
    .gt-body-section { padding: 30px 0 30px; }
    .gt-hero__img-wrap img { max-height: 366px; width: 100%; }
    .gt-hero__title { font-size: 26px; line-height: 120%; }
    .gt-para { font-size: 14px; margin-bottom: 14px; }
    .gt-subhead--bold { font-size: 14px; margin-bottom: 14px; }

    .gt-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .gt-newsletter__mic-img { width: 100%; height: 366px; object-fit: cover; }
    .gt-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .gt-newsletter__content {
      padding: 30px 16px 40px;
      text-align: center;
    }
    .gt-newsletter__heading { font-size: 30px; line-height: 120%; }
    .gt-newsletter__sub { font-size: 14px; max-width: 275px; margin: 0 auto 24px; }
    .gt-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 15px;
      margin-top: 15px;
    }
    .gt-newsletter__input { border-radius: 5px; height: 40px; font-size: 14px; }
    .gt-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 40px;
      font-size: 14px;
    }
  }
`;

/* ── SCROLL REVEAL ── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".gt-fade-up");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("gt-in"); }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ── ARROW ── */
function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ── NEWSLETTER ── */
function Newsletter({ isMobile }) {
  const [email, setEmail] = useState("");
  return (
    <section className="gt-newsletter">
      {!isMobile && (
        <>
          <img className="gt-newsletter__bg" src={NewsletterBgDesktop} alt="" aria-hidden="true" />
          <div className="gt-newsletter__overlay" />
        </>
      )}
      <div className="gt-newsletter__inner">
        {isMobile && (
          <img className="gt-newsletter__mic-img" src={NewsletterMicMobile} alt="Microphone" />
        )}
        <div className="gt-newsletter__content">
          <h2 className="gt-newsletter__heading">Sign up for our<br />newsletter!</h2>
          <p className="gt-newsletter__sub">Get notified about updates and be the first to get early access to new episodes.</p>
          <div className="gt-newsletter__form">
            <input
              className="gt-newsletter__input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="gt-newsletter__btn">
              Subscribe
              <span className="gt-newsletter__btn-arrow"><ArrowRight /></span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── ROOT EXPORT ── */
export default function WhyWeMustUnlockEnergysDNAToEnableAGreenTransition() {
  useScrollReveal();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [isSmallMobile, setIsSmallMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    setIsSmallMobile(mq.matches);
    const handler = (e) => setIsSmallMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className="gt-wrap">

        {/* HERO — centered title */}
        <section className="gt-hero">
          <div className="gt-hero__title-bar">
            <div className="gt-inner">
              <h1 className="gt-hero__title">Why we must unlock energy's DNA to enable a green transition</h1>
            </div>
          </div>
          <div className="gt-hero__img-wrap">
            <img
              src={isSmallMobile ? HeroImgMobile : HeroImg}
              alt="Energy DNA Green Transition"
            />
          </div>
        </section>

        {/* BODY */}
        <section className="gt-body-section">
          <div className="gt-inner">
            <div className="gt-content-col">

              {/* bold opening sentence — image 2 */}
              <p className="gt-subhead--bold gt-fade-up gt-delay-1">Europe can emerge from the coronavirus pandemic with a significantly clearer pathway to a net-zero future if it takes the right actions now.</p>

              <p className="gt-para gt-fade-up gt-delay-1">That's according to Roberto Castiglioni, the co-founder and chief executive of London-headquartered sustainable investment consultancy Ikigai Group.</p>

              <p className="gt-para gt-fade-up gt-delay-1">He said the "silver lining to Covid" was the knock-on effect it would have on the energy sector.</p>

              <p className="gt-para gt-fade-up gt-delay-1">Castiglioni was speaking this morning at the Enlit Europe Virtual Keynote, which posed the question: What if the pandemic accelerated the green transition?</p>

              <p className="gt-para gt-fade-up gt-delay-1">He said that while the pandemic had caused a negative impact to the global economy, it had delivered a positive effect to worldwide global energy demand by reducing it and also bringing forward the peak of energy-related emissions by about five years.</p>

              <p className="gt-para gt-fade-up gt-delay-1">However, he added that in the long term, market forces alone would not bring about decarbonization in "hard-to-abate sectors" such as heavy industries like chemicals, steel and cement, and transportation, particularly aviation: "We don't want to stop flying — we just want to fly in a sustainable way."</p>

              <p className="gt-para gt-fade-up gt-delay-1">"We need to scale up technologies that can deliver a decarbonized, Paris-compliant future," he said, adding that alongside this was a need for policies to commercialize and scale alternative fuels such as hydrogen and energy-efficiency measures, including carbon capture, storage and utilization.</p>

              <p className="gt-para gt-fade-up gt-delay-1">He stressed that "one thing is for sure: we are living through an energy revolution. We have had the Industrial Revolution and a communications revolution: now we have the 'Energy Revolution'. Where we are today will be totally different from where we will be in five years."</p>

            </div>
          </div>
        </section>

        {/* NEWSLETTER */}
        <Newsletter isMobile={isMobile} />

      </div>
    </>
  );
}